import { API_URL } from "../config/api";

export interface MaterialTrackerItem {
  id?: string; // UUID | None = None
  project_id: string; // UUID (required)
  item: string;
  name: string;
  description: string;
  quantity: number; // int
  ordered_at: string; // datetime
  eta: string; // datetime - estimated time of arrival
  tracking_number?: string | null; // str | None = None
  lead_time?: string | null; // str | None = None
  esd: string; // datetime - estimated ship date
  isd: string; // datetime
  status?: 'pending' | 'ordered' | 'delivered' | 'delayed'; // Material status
  created_at?: string; // datetime - when material was added to project
  updated_at?: string; // datetime - when material was last updated
}

export interface MaterialTrackerResponse {
  total: number;
  page: number;
  size: number;
  results: MaterialTrackerItem[];
}

/**
 * Get project materials with pagination
 */
export const getProjectMaterials = async (
  token: string,
  projectId: string,
  page: number = 1,
  size: number = 100
): Promise<MaterialTrackerResponse> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });

    const response = await fetch(`${API_URL}/projects/${projectId}/materials?${params.toString()}`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: MaterialTrackerResponse = await response.json();
    return data;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Failed to fetch project materials');
  }
};

/**
 * Create project material
 */
export const createProjectMaterial = async (
  token: string,
  projectId: string,
  material: Omit<MaterialTrackerItem, 'id' | 'project_id'>
): Promise<MaterialTrackerItem> => {
  try {
    // Add project_id to the material data since it's required by the API
    const materialWithProjectId = {
      ...material,
      project_id: projectId
    };

    const response = await fetch(`${API_URL}/projects/${projectId}/materials`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(materialWithProjectId)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: MaterialTrackerItem = await response.json();
    return data;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Failed to create project material');
  }
};

/**
 * Get specific project material
 */
export const getProjectMaterial = async (
  token: string,
  projectId: string,
  materialId: string
): Promise<MaterialTrackerItem> => {
  try {
    const response = await fetch(`${API_URL}/projects/${projectId}/materials/${materialId}`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: MaterialTrackerItem = await response.json();
    return data;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Failed to fetch project material');
  }
};

/**
 * Safe JSON parsing with empty response handling
 */
const safeJsonParse = async (response: Response): Promise<any> => {
  const text = await response.text();
  if (!text || text.trim() === '') {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch (error) {
    console.warn('Failed to parse JSON response:', text);
    throw new Error('Invalid JSON response from server');
  }
};

/**
 * Update project material
 */
export const updateProjectMaterial = async (
  token: string,
  projectId: string,
  materialId: string,
  material: Partial<Omit<MaterialTrackerItem, 'id' | 'project_id'>>
): Promise<MaterialTrackerItem> => {
  try {
    // Add project_id to the material data to ensure consistency
    const materialWithProjectId = {
      ...material,
      project_id: projectId
    };

    const response = await fetch(`${API_URL}/projects/${projectId}/materials/${materialId}`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(materialWithProjectId)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await safeJsonParse(response);
    
    // If server returns empty response, return the updated material data
    if (!data) {
      return {
        id: materialId,
        project_id: projectId,
        ...material
      } as MaterialTrackerItem;
    }
    
    return data;
  } catch (error: Error | any) {
    if (error.message?.includes('JSON')) {
      throw new Error('Server returned invalid response. The material may have been updated successfully.');
    }
    throw new Error(error.message || 'Failed to update project material');
  }
};

/**
 * Delete project material
 */
export const deleteProjectMaterial = async (
  token: string,
  projectId: string,
  materialId: string
): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/projects/${projectId}/materials/${materialId}`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    // DELETE operations typically don't return content, so we don't try to parse JSON
    return;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Failed to delete project material');
  }
}; 